import { formatReportDate } from '../reports/formatting';
import { DIMENSION_LABEL } from './exportOptions';
import { categoryLabel, type AccountingMapping, type ExportSummary, type UnmappedItem } from './exportTypes';

export { formatReportDate as formatDate };

/** "2026-10-16T10:15:00Z" -> "Oct 16, 2026, 10:15 AM UTC". Always UTC so the same export reads the same everywhere. */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return `${date.toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: 'UTC' })} UTC`;
}

export const periodLabel = (start: string, end: string): string => `${formatReportDate(start)} to ${formatReportDate(end)}`;

/** "FederalIncomeTax" -> "Federal income tax". */
export function humanize(value: string): string {
  return value.replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase().replace(/^./, (ch) => ch.toUpperCase());
}

/**
 * A subcategory in words. The payroll classifies federal income tax, Social Security and Medicare (as one), FUTA, state and local income tax, state unemployment,
 * paid family and medical leave / disability and other employer tax; any other tax type arrives as `Other`, which only means something together with its payroll
 * code, so the code is shown instead.
 */
export function subcategoryLabel(subcategory: string, payrollCode?: string | null): string {
  if (subcategory === 'Other') return payrollCode ? `${payrollCode} (other tax type)` : 'Other';
  if (subcategory === 'SocialSecurityMedicare') return 'Social Security and Medicare';
  return humanize(subcategory);
}

/** "Employee tax withholding payable: Federal income tax" for a category with an optional subcategory (and its payroll code, which names an `Other`). */
export function categoryWithSub(category: string, subcategory: string | null, payrollCode?: string | null): string {
  return subcategory ? `${categoryLabel(category)}: ${subcategoryLabel(subcategory, payrollCode)}` : categoryLabel(category);
}

export function unmappedLabel(item: UnmappedItem): string {
  const parts = [categoryWithSub(item.category, item.subcategory, item.payrollCode)];
  if (item.payrollCode && item.subcategory !== 'Other') parts.push(`code ${item.payrollCode}`);
  return parts.join(', ');
}

export const unmappedReasonLabel: Record<string, string> = {
  NoMapping: 'No mapping',
  Ambiguous: 'Ambiguous: two equally specific mappings apply',
  ArchivedAccount: 'The mapped account is archived',
};

/** What a mapping applies to, most specific first (employee, code, department, location), or "Company default". */
export function mappingScope(m: AccountingMapping, names?: { employees?: Map<string, string>; departments?: Map<string, string>; groups?: Map<string, string> }): string {
  const parts: string[] = [];
  if (m.employeeId) parts.push(`Employee ${names?.employees?.get(m.employeeId) ?? m.employeeId}`);
  if (m.groupDimension && m.groupKey) {
    const label = names?.groups?.get(`${m.groupDimension}:${m.groupKey}`) ?? (m.groupDimension === 'Department' ? names?.departments?.get(m.groupKey) : undefined) ?? m.groupKey;
    parts.push(`${DIMENSION_LABEL[m.groupDimension]} group ${label}`);
  }
  if (m.payrollCode) parts.push(`Code ${m.payrollCode}`);
  if (m.departmentId) parts.push(`Department ${names?.departments?.get(m.departmentId) ?? m.departmentId}`);
  if (m.locationCode) parts.push(`Location ${m.locationCode}`);
  const scope = parts.length > 0 ? parts.join(', ') : 'Company default';
  return m.subcategory ? `${scope} (${subcategoryLabel(m.subcategory, m.payrollCode)})` : scope;
}

/** A short form of a checksum for a table cell (the full value is always in the export's detail). */
export const shortChecksum = (checksum: string): string => (checksum ? `${checksum.slice(0, 12)}…` : 'None');

/** Statuses in which an export can be reversed / replaced (contract section 8). */
export const canBeReversed = (e: Pick<ExportSummary, 'status' | 'kind'>): boolean =>
  e.kind !== 'Reversal' && (e.status === 'Generated' || e.status === 'Downloaded' || e.status === 'ReversalRequired');
export const canBeReplaced = (e: Pick<ExportSummary, 'status' | 'kind'>): boolean => e.kind !== 'Reversal' && e.status === 'Reversed';

/** A blocked or in-doubt export a person should look at in History. */
export const needsAttention = (status: string): boolean => status === 'ReversalRequired' || status === 'Reversed' || status === 'Failed';
